import moment from "moment";
import { tw } from "@careinfo/tailwind";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import CommentIcon from "../../assets/CommentIcon";
import CrossIcon from "../../assets/CrossIcon";
import useGetHook from "../../customHooks/useGetHook";
import usePostHook from "../../customHooks/usePostHook";
import { responsiveClassName } from "../../utils/classNames";
import RecursiveComment from "../RecursiveComment";
import APIS from "../../constant/Endpoint";
import { toast } from "react-toastify";
import MapIcon from "../../Icons/MapIcon";
import {
  File,
  FileArchive,
  FileAudio,
  FileCheck,
  FileImage,
  FileText,
  FileVideo,
  FileX,
  PlusIcon,
} from "lucide-react";
import useUploadFileHook from "../../customHooks/useUploadFileHook";
import Message from "../Notification/Chat/Message";
import Comment from "./Comment";
import { deleteApiData, getApiData } from "../../helpers/AxiosInstance";

const OrderHistory = ({ selectedOrder }) => {
  const { handleSubmit, register, setError, reset } = useForm({});
  const [comment, setComment] = useState<any>();
  const [deliveredAddress, setDeliveredAddress] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const { data: remarks, refetch } = useGetHook({
    queryKey: [`remakrs-${selectedOrder}`],
    url: APIS.COMMENT,
    params: {
      limit: 200,
      offset: 0,
      object_id: selectedOrder,
    },
  });
  const { data: profile, isLoading: isprofiledataLoading } = useGetHook({
    queryKey: [`profile`],
    url: `${APIS.USER}me/`,
    params: {},
  });
  const { data: orderHistory } = useGetHook({
    queryKey: [`order-hsitory-${selectedOrder}`],
    url: APIS.ORDER_HISTORY,
    params: {
      limit: 200,
      offset: 0,
      field_name: "delivery_status",
      object_id: selectedOrder,
    },
  });

  const { data: runsheetData } = useGetHook({
    queryKey: [`runsheet-${selectedOrder}`],
    url: `${APIS.RUNSHEET_ORDER}`,
    params: {
      order_id: selectedOrder,
    },
  });
  const { data: InvoiceData } = useGetHook({
    queryKey: [`invoice-${selectedOrder}`],
    url: `${APIS.INVOICE}`,
    params: {
      order_id: selectedOrder,
    },
  });
  const { data: ReceiverDetails } = useGetHook({
    queryKey: [`receiver-details-${selectedOrder}`],
    url: APIS.RECEIVER_DETAIL,
    params: {
      order: selectedOrder,
    },
  });

  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: [`remakrs-${selectedOrder}`],
  });
  const { mutateAsync: updateFollow } = usePostHook({
  queryKey: [`update-follow-${selectedOrder}`],
});

  const handleCreateReview = async (data: any, id: any) => {
    const url = `${APIS.COMMENT}`;
    const isFile =
      typeof data?.comment === "string" && data?.comment.startsWith("media");

    let formData = {
      ...data,
      type: isFile ? "file" : "text",
      branch: profile?.default_branch?.id,
      parent: id,
      object_id: selectedOrder,
    };
    try {
       await updateFollow({
      url:`${APIS.ORDER}${selectedOrder}/update-last-follow/`,
      formData: {
        selectedOrder
      },
      setError,
    });
      const res = await mutateAsync({ url, formData, setError });
      if (res?.status === 201) {
        reset();
        setComment(null);
        setCommentText("");
        setPreviewUrl(null);
        refetch();
      }
    } catch (err) {
      if (err instanceof Error) toast.error(err.message);
    }
  };

  const handleDownload = async () => {
    const fileURL = ReceiverDetails?.[0]?.identity_image;
    const fullName = ReceiverDetails?.[0]?.full_name;

    if (!fileURL || !fullName) {
      console.error("Invalid file URL or Receiver details.");
      return;
    }

    try {
      const response = await fetch(fileURL, {
        mode: "cors",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch the image");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      const extension = blob.type.split("/")[1] || "jpg";
      link.download = `Receiver Document - ${fullName}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading the image:", error);
    }
  };

  const [commentText, setCommentText] = useState(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [commentText]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (commentText.trim()) {
        handleSubmit(() =>
          handleCreateReview({ comment: commentText }, comment?.id)
        )();
        setCommentText("");
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value);
  };

  const { uploadFile, progress } = useUploadFileHook({
    setIsUploading,
    queryKey: ["file"],
  });

  const generateFileId = () => {
    return (
      "file_" +
      Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!allowedTypes.includes(file.type)) {
      alert("Only images, PDF, Word, and Excel files are allowed.");
      return;
    }

    setSelectedFile(file);
    const fileURL = URL.createObjectURL(file);
    setPreviewUrl(fileURL);
    handleUpload(file);
  };

  const handleUpload = async (file: File) => {
    const fileId = generateFileId();
    const res = await uploadFile(file, fileId, selectedOrder);
    setCommentText(res);
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const getFileIconComponent = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "pdf":
      case "doc":
      case "docx":
      case "txt":
        return <FileText className={tw`w-8 h-8 text-blue-600`} />;
      case "png":
      case "jpg":
      case "jpeg":
      case "gif":
        return <FileImage className={tw`w-8 h-8 text-green-600`} />;
      case "mp3":
      case "wav":
        return <FileAudio className={tw`w-8 h-8 text-purple-600`} />;
      case "mp4":
      case "avi":
        return <FileVideo className={tw`w-8 h-8 text-pink-600`} />;
      case "zip":
      case "rar":
      case "7z":
        return <FileArchive className={tw`w-8 h-8 text-yellow-600`} />;
      case "json":
        return <FileCheck className={tw`w-8 h-8 text-teal-600`} />;
      case "exe":
        return <FileX className={tw`w-8 h-8 text-red-600`} />;
      default:
        return <File className={tw`w-8 h-8 text-gray-500`} />;
    }
  };

  const getAddressFromCoordinates = async (
    latitude: number,
    longitude: number
  ) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
      );
      const data = await response.json();
      if (data?.address) {
        const { city_district, town, village, suburb, neighbourhood } =
          data.address;
        const locationParts = [
          neighbourhood,
          suburb,
          city_district || town || village,
        ]
          .filter(Boolean)
          .join(", ");
        setDeliveredAddress(locationParts || "Location not found");
      } else {
        setDeliveredAddress("Location not found");
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      setDeliveredAddress("Error fetching location");
    }
  };

  useEffect(() => {
    if (!ReceiverDetails?.[0]?.delivered_coordinate) {
      return;
    }
    getAddressFromCoordinates(
      ReceiverDetails?.[0]?.delivered_coordinate?.latitude,
      ReceiverDetails?.[0]?.delivered_coordinate?.longitude
    );
  }, [ReceiverDetails]);

  const handleDeleteComment = async (id: string) => {
    const url = `${APIS.COMMENT}${id}/`;
    try {
      const res = await deleteApiData({ url, setError: () => {} });
      if (res?.status === 204) {
        toast.success(res?.message);
        refetch();
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <div className={tw` bg-white py-2 `}>
        {deliveredAddress && (
          <a
            href={`https://www.google.com/maps?q=${ReceiverDetails?.[0]?.delivered_coordinate?.latitude},${ReceiverDetails?.[0]?.delivered_coordinate?.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className={tw`flex items-center font-semibold`}
          >
            Delivered Location:
            <span className={tw`ml-1 hover:underline`}>{deliveredAddress}</span>
            <MapIcon />
          </a>
        )}
        <div
          className={responsiveClassName(
            "grid grid-cols-1 gap-x-4",
            "grid grid-cols-2 gap-x-4"
          )}
        >
          <div className={tw`container mx-auto mt-5 overflow-y-auto`}>
            <table className={tw`min-w-full bg-white border`}>
              <thead>
                <tr
                  className={tw`bg-gray-200 text-gray-600 uppercase text-[12px] leading-normal`}
                >
                  <th className={tw`py-2 px-6 text-left border `} colSpan={2}>
                    Main Order
                  </th>
                  <th className={tw`py-2 px-6 text-left border`} colSpan={2}>
                    Rider Information
                  </th>
                </tr>
                <tr
                  className={tw`text-gray-600 uppercase text-[12px] leading-normal`}
                >
                  <th className={tw`py-2 px-6 text-left border`}>Inv ID</th>
                  <th className={tw`py-2 px-6 text-left border`}>
                    Runsheet ID
                  </th>
                  <th className={tw`py-2 px-6 text-left border`}>Name</th>
                  <th className={tw`py-2 px-6 text-left border`}>Phone no.</th>
                </tr>
              </thead>
              <tbody className={tw`text-gray-600 text-[12px] font-light`}>
                <tr className={tw`border-b border-gray-200 hover:bg-gray-100`}>
                  <th className={tw`py-2 px-6 text-left border`}>
                    {InvoiceData?.invoice_id ?? "N/A"}
                  </th>
                  <th className={tw`py-2 px-6 text-left border`}>
                    {runsheetData?.runsheet_id ?? "N/A"}
                  </th>
                  <th className={tw`py-2 px-6 text-left border`}>
                    {runsheetData?.name ?? "N/A"}
                  </th>
                  <th className={tw`py-2 px-6 text-left border`}>
                    {runsheetData?.phone_number ?? "N/A"}
                  </th>
                </tr>
              </tbody>
            </table>
          </div>
          <div className={tw`container mx-auto mt-5 overflow-y-auto`}>
            <table className={tw`min-w-full bg-white border`}>
              <thead>
                <tr
                  className={tw`bg-gray-200 text-gray-600 uppercase text-[12px] leading-normal`}
                >
                  <th className={tw`py-2 px-6 text-left border`} colSpan={2}>
                    <p>Proof of Delivery </p>
                  </th>
                  <th className={tw`py-2 px-6 text-left border `} colSpan={2}>
                    {ReceiverDetails?.[0]?.identity_image ? (
                      <button
                        className={tw`focus:outline-none text-right cursor-pointer`}
                        onClick={() => handleDownload()}
                      >
                        Download File
                      </button>
                    ) : null}
                  </th>
                </tr>
                <tr
                  className={tw`text-gray-600 uppercase text-[12px] leading-normal `}
                >
                  <th className={tw`py-2 px-6 text-left border`}>Name</th>
                  <th className={tw`py-2 px-6 text-left border`}>Phone No.</th>
                  <th className={tw`py-2 px-6 text-left border`}>id</th>
                  <th className={tw`py-2 px-6 text-left border`}>Signature</th>
                </tr>
              </thead>
              <tbody className={tw`text-gray-600 text-[12px] font-light`}>
                <tr className={tw`border-b border-gray-200 hover:bg-gray-100`}>
                  <th className={tw`py-2 px-6 text-left border`}>
                    {ReceiverDetails?.[0]?.full_name ?? "N/A"}
                  </th>
                  <th className={tw`py-2 px-6 text-left border`}>
                    {ReceiverDetails?.[0]?.primary_contact ?? "N/A"}
                  </th>
                  <th className={tw`py-2 px-6 text-left border`}>
                    <p className={tw`uppercase`}>
                      {ReceiverDetails?.[0]?.identity}
                    </p>
                    <p>{ReceiverDetails?.[0]?.identity_number ?? "N/A"}</p>
                  </th>
                  <th className={tw`border bg-white  `}>
                    {ReceiverDetails?.[0]?.signature ? (
                      <img
                        src={ReceiverDetails?.[0]?.signature}
                        alt=""
                        className={tw`object-contain h-20 w-full`}
                      />
                    ) : (
                      ""
                    )}
                  </th>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div
        className={responsiveClassName(
          "grid grid-cols-1  text-lg font-semibold",
          "grid grid-cols-2  text-2xl font-semibold"
        )}
      >
        <div className={tw` `}>
          <p>Order History</p>
          <div className={tw`max-h-[50svh] overflow-y-auto `}>
            <div className={tw`flex flex-col md:grid text-gray-50 mt-2`}>
              {orderHistory?.results?.map((item: Record<string, any>) => (
                <div className={tw`flex items-contents`} key={item.id}>
                  <div
                    className={responsiveClassName(
                      "hidden",
                      "col-start-2 col-end-4 mr-10 md:mx-auto relative "
                    )}
                  >
                    <div
                      className={tw`h-full w-6 flex items-center justify-center`}
                    >
                      <div
                        className={tw`h-full w-1 bg-green-500 pointer-events-none`}
                      ></div>
                    </div>
                    <div
                      className={tw`w-6 h-6 absolute top-1/2 -mt-3 rounded-full bg-green-500 shadow text-center`}
                    >
                      <i className={tw`fas fa-check-circle text-white`}></i>
                    </div>
                  </div>
                  <div
                    className={tw`bg-green-500 col-start-4 col-end-12 px-2 py-1 rounded-lg my-2 mr-auto shadow-md w-full `}
                  >
                    <h3 className={tw`font-semibold text-sm`}>
                      {item?.extra_data?.comment ?? item?.new_data}
                      {item?.user?.name && (
                        <>
                          &nbsp;by&nbsp;
                          {item?.user?.name}
                        </>
                      )}
                    </h3>
                    <div
                      className={responsiveClassName(
                        `flex flex-col text-xs  whitespace-nowrap w-full`,
                        "flex justify-between text-xs items-center whitespace-nowrap w-full"
                      )}
                    >
                      <div className={tw`flex gap-x-1 items-center`}>
                        <p>{item?.branch?.name}</p>
                        <p>{item?.extra_data?.phone_number}</p>
                      </div>
                      <p className={tw`leading-tight `}>
                        {item?.extra_data?.date
                          ? moment(item?.extra_data?.date).format(
                              "YY-MM-DD h:mm:ss A"
                            )
                          : moment(item?.created).format("YY-MM-DD h:mm:ss A")}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <p>Remarks</p>
          <div className={tw`text-xs md:text-md`}>
            
            <form
              className={tw`px-4 py-2 w-full border-t border-gray-300 bg-white`}
              onSubmit={(e) => {
                e.preventDefault();
                if (commentText.trim()) {
                  handleCreateReview({ comment: commentText }, comment?.id);
                  setCommentText("");
                }
              }}
            >
              {comment && (
                <div className={tw`mb-2 border-t pt-2`}>
                  <div className={tw`flex justify-between items-center`}>
                    <p className={tw`text-sm text-gray-700`}>
                      Replying to{" "}
                      <span className={tw`font-medium`}>
                        {comment?.user?.name}
                      </span>
                    </p>
                    <div
                      className={tw`cursor-pointer text-gray-500`}
                      onClick={() => setComment("")}
                    >
                      <CrossIcon />
                    </div>
                  </div>
                  <p className={tw`text-gray-500 text-xs ml-2`}>
                    {/* {comment?.comment} */}
                    <Comment comment={comment.comment} />
                  </p>
                </div>
              )}
              <div className={tw`flex items-center gap-2`}>
                <label
                  title="Upload Files"
                  className={tw`cursor-pointer hover:bg-gray-200 rounded-full text-gray-600 `}
                >
                  <PlusIcon className={tw`w-5 h-5`} />
                  <input
                    type="file"
                    hidden
                    onChange={handleFileChange}
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*"
                  />
                </label>
                {previewUrl ? (
                  <div className={tw`mb-2 w-full border rounded-lg relative`}>
                    {selectedFile?.type.startsWith("image/") ? (
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className={tw`w-20 h-20 object-fit rounded-lg border border-gray-300`}
                      />
                    ) : (
                      <div
                        className={tw`text-sm flex items-center gap-x-2 text-gray-600 bg-gray-100 p-2 rounded-md`}
                      >
                        {getFileIconComponent(selectedFile?.name)}
                        {selectedFile?.name} (
                        {Math.round(selectedFile?.size / 1024)} KB)
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={isUploading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setCommentText("");
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                      className={tw`absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full w-5 h-5 flex items-center justify-center text-xs text-gray-600 hover:bg-gray-300  transition`}
                      aria-label="Remove file"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <textarea
                    ref={textareaRef}
                    placeholder="Type a comment..."
                    value={commentText}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    className={tw`flex-1 text-sm border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary text-gray-700 overflow-hidden resize-none`}
                  />
                )}
                <button
                  type="submit"
                  disabled={isPostLoading || isUploading}
                  className={tw`text-primary hover:text-black focus:outline-none`}
                >
                  <CommentIcon />
                </button>
              </div>
            </form>
            <div className={tw`max-h-[40svh] overflow-y-auto `}>
              {remarks?.results?.map((item, index) => (
                <RecursiveComment
                  key={item?.id}
                  item={item}
                  setComment={setComment}
                  handleDelete={handleDeleteComment}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderHistory;
